<template>
  <div>
    <ui-dropdown
      :model-value="modelValue"
      :options="normalizedOptions"
      :mode="Modes.Multiple"
      :is-loading="isLoading"
      :search-placeholder="$t('universal.search')"
      :loading-text="$t('pickers.loading_results')"
      :no-results-text="$t('pickers.helpdesk_custom_field_values.no_results')"
      :total="total"
      has-search
      filter-results
      reorder-after-select
      size="lg"
      :is-object="true"
      full-width
      label="label"
      prop-to-check="value"
      append-to-document
      @search-change="handleSearch"
      @update:model-value="emit('update:modelValue', $event)"
    >
      <template #trigger-multiple-selected="{ count }">
        {{ $t('universal.items_selected', Number(count)) }}
      </template>

      <template #trigger="{ active }">
        <ui-menu-select-trigger small :active>
          <template v-if="getFieldLabel(modelValue)">
            {{ getFieldLabel(modelValue) }}
          </template>
          <span v-else class="text-gray-50">{{ $t('filter.multiple_value_placeholder') }}</span>
        </ui-menu-select-trigger>
      </template>
      <template #more-count="{ count }">
        {{ $t('universal.n_more_simple', { n: $n(count) }, count) }}
      </template>
    </ui-dropdown>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { debounce } from 'lodash-es'
import type { CustomField } from '@zendesk/zqa-services/filters'
import { Modes } from '@klausapp/ui-kit/components/UiDropdownV2/types'
import { UiDropdownV2 as UiDropdown, UiMenuSelectTrigger } from '@klausapp/ui-kit'

import { session } from '@/composables/useSession'
import useHelpdeskCustomFieldValues from '@/modules/shared/filters/useHelpdeskCustomFieldValues'
import i18n from '@/i18n'

import type { DropdownSelectedValue } from './types'

const props = defineProps<{
  modelValue: DropdownSelectedValue[]
  fieldExternalId: CustomField['externalId']
  fieldName: CustomField['name']
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: DropdownSelectedValue[]): void
}>()

const searchQuery = ref('')
const handleSearch = debounce((search: string) => (searchQuery.value = search), 300)

const workspaceIds = computed(() => [session.workspace.id])

const { options, total, isLoading } = useHelpdeskCustomFieldValues(
  workspaceIds,
  computed(() => {
    return {
      customFieldId: props.fieldExternalId,
      search: searchQuery.value,
      limit: undefined,
      includeValues: props.modelValue.map((v) => v.value),
    }
  }),
)

const normalizedOptions = computed(() => {
  if (!options.value) return []
  const normalized = options.value.map((option) => ({
    label: option.label,
    value: option.value,
    name: option.value,
    isCustom: true,
    fieldId: props.fieldExternalId,
  }))

  // Make sure we always show selected values even if they are not included in the fetched options
  // This happens when users have more custom values than set limit of 50 and they search and select a value that is not in the first 50
  const selectedValues = props.modelValue.filter((mv) => normalized.every((no) => no.value !== mv.value))

  return [...selectedValues, ...normalized]
})

const getFieldLabel = (values) => {
  if (!values.length) return null
  if (values.length === 1) return values[0].label

  return i18n.t('universal.items_selected', values.length)
}
</script>
